import { cloneDeep } from 'lodash'
import {
  GFPP_ACTION_IDS,
  getAppBaseUrl,
  DATASET,
  ROUTER_DATASET,
} from '@wix/wix-data-client-common'

// See https://github.com/wix-private/js-platform-editor-sdk/blob/master/js/manifest.ts
const ALWAYS_VISIBLE = 'EDITOR' // The value is undocumented.
const NEVER_VISIBLE = 'NEVER'

export default async (
  t,
  experimentsManager,
  editorSdkProxy,
  bindingDefinitions,
  features,
) => {
  const showDataset = !features.inspectorXP && !features.editor3orStudio2

  const visibility = showDataset ? ALWAYS_VISIBLE : NEVER_VISIBLE

  const routerManifest = {
    visibility,
    connections: createConnectionsManifest({
      t,
      bindingDefinitions,
    }),
    enableShowOnAllPages: false,
    removable: false,
    duplicatable: false,
    gfpp: createFullGfppConfig({ t }),
  }
  const baseUrl = await getAppBaseUrl(editorSdkProxy)
  return {
    [DATASET]: {
      default: {
        visibility,
        ...(showDataset && {
          icon: makeUrlToSvg({
            baseUrl,
            fileName: 'dataset_unconfigured',
          }),
        }),
        gfpp: createSettingsOnlyGfppConfig({ t }),
      },

      hidden: {
        visibility: NEVER_VISIBLE,
        connections: createConnectionsManifest({
          t,
          bindingDefinitions,
        }),
        gfpp: createFullGfppConfig({ t }),
      },

      configured: {
        visibility,
        connections: createConnectionsManifest({
          t,
          bindingDefinitions,
        }),
        ...(showDataset && {
          icon: makeUrlToSvg({
            baseUrl,
            fileName: 'dataset_configured',
          }),
        }),
        gfpp: createFullGfppConfig({ t }),
      },

      connectedToHiddenAppCollection: {
        visibility,
        connections: createConnectionsManifest({
          t,
          bindingDefinitions,
        }),
        ...(showDataset && {
          icon: makeUrlToSvg({
            baseUrl,
            fileName: 'dataset_unconfigured',
          }),
        }),
        gfpp: createFullGfppConfig({ t }),
      },

      connectedToDeletedCollection: {
        visibility,
        connections: createConnectionsManifest({
          t,
          bindingDefinitions,
        }),
        ...(showDataset && {
          icon: makeUrlToSvg({
            baseUrl,
            fileName: 'dataset_error',
          }),
        }),
        gfpp: createFullGfppConfig({ t }),
      },
    },

    [ROUTER_DATASET]: {
      default: {
        ...routerManifest,
        ...(showDataset && {
          icon: makeUrlToSvg({
            baseUrl,
            fileName: 'router_dataset_configured',
          }),
        }),
      },

      configured: {
        ...routerManifest,
        ...(showDataset && {
          icon: makeUrlToSvg({
            baseUrl,
            fileName: 'router_dataset_configured',
          }),
        }),
      },

      hidden: {
        ...routerManifest,
        visibility: NEVER_VISIBLE,
      },

      connectedToHiddenAppCollection: {
        ...routerManifest,
        ...(showDataset && {
          icon: makeUrlToSvg({
            baseUrl,
            fileName: 'router_dataset_unconfigured',
          }),
        }),
      },

      connectedToDeletedCollection: {
        ...routerManifest,
        ...(showDataset && {
          icon: makeUrlToSvg({
            baseUrl,
            fileName: 'router_dataset_error',
          }),
        }),
      },

      brokenConfig: {
        ...routerManifest,
        ...(showDataset && {
          icon: makeUrlToSvg({
            baseUrl,
            fileName: 'router_dataset_error',
          }),
        }),
      },

      removableController: {
        visibility,
        removable: true,
      },
    },
  }
}

function createConnectionsManifest({ t, bindingDefinitions }) {
  return Object.entries(bindingDefinitions).reduce(
    (connections, [_type, binding]) => {
      const { sections } = binding

      sections.forEach(sectionDefinition => {
        const { role, subroles = [], manifest } = sectionDefinition

        if (manifest) {
          connections[role] = translateManifestLabels({ t, manifest })
        }
        subroles.forEach(subrole => {
          if (subrole.manifest) {
            connections[subrole.name] = translateManifestLabels({
              t,
              manifest: subrole.manifest,
            })
          }
        })
      })
      return connections
    },
    {},
  )
}

function translateManifestLabels({ t, manifest }) {
  const translatedManifest = cloneDeep(manifest)

  const mainAction1 = translatedManifest?.gfpp?.desktop?.mainAction1
  if (mainAction1?.label) {
    mainAction1.label = t(mainAction1.label)
  }

  const mainAction2 = translatedManifest?.gfpp?.desktop?.mainAction2
  if (mainAction2?.label) {
    mainAction2.label = t(mainAction2.label)
  }

  return translatedManifest
}

function makeUrlToSvg({ baseUrl, fileName }) {
  return `${baseUrl}/${fileName}.svg`
}

function createFullGfppConfig({ t }) {
  return {
    mainAction1: createManageContentAction({ t }),
    mainAction2: createSettingsAction({ t }),
  }
}

function createManageContentAction({ t }) {
  return {
    actionId: GFPP_ACTION_IDS.MANAGE_CONTENT_ACTION,
    label: t('gfpp_mainaction_dataset_manage_content'),
  }
}

function createSettingsOnlyGfppConfig({ t }) {
  return { mainAction1: createSettingsAction({ t }) }
}

function createSettingsAction({ t }) {
  return {
    actionId: GFPP_ACTION_IDS.SETTINGS_ACTION,
    label: t('gfpp_mainaction_dataset_settings'),
  }
}
